(() => {
  'use strict';

  if (window.VexxPdpAjaxCartInstalled) return;
  window.VexxPdpAjaxCartInstalled = true;

  const SELECTORS = {
    productForm: '.pdp-form, [data-quick-view-form]',
    submitButton: '.pdp-add-button, [data-quick-view-add], button[type="submit"]',
    productTitle: '.quick-view__title, .pdp-title, .product__title, h1',
    cartBadge: '[data-open-cart] .badge',
    siteHeader: '#site-header',
    cartDrawer: '#cart-drawer',
  };

  const getShopRoot = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const getProductTitle = (form) => {
    const scope = form.closest('#vexx-quick-view, .pdp-layout, main, [data-section-id]') || document;
    const title = scope.querySelector(SELECTORS.productTitle)?.textContent?.trim();
    return title || 'Your item';
  };

  const getToastRegion = () => {
    let region = document.getElementById('vexx-add-to-cart-toast-region');
    if (region) return region;

    region = document.createElement('div');
    region.id = 'vexx-add-to-cart-toast-region';
    region.className = 'vexx-cart-toast-region';
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    document.body.appendChild(region);
    return region;
  };

  const showToast = ({ type = 'success', title, message }) => {
    const region = getToastRegion();
    region.replaceChildren();

    const toast = document.createElement('div');
    toast.className = `vexx-cart-toast vexx-cart-toast--${type}`;
    toast.setAttribute('role', type === 'error' ? 'alert' : 'status');

    const icon = document.createElement('span');
    icon.className = 'vexx-cart-toast__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = type === 'error' ? '!' : '✓';

    const copy = document.createElement('div');
    copy.className = 'vexx-cart-toast__copy';

    const heading = document.createElement('strong');
    heading.className = 'vexx-cart-toast__title';
    heading.textContent = title;

    const body = document.createElement('span');
    body.className = 'vexx-cart-toast__message';
    body.textContent = message;

    const close = document.createElement('button');
    close.className = 'vexx-cart-toast__close';
    close.type = 'button';
    close.setAttribute('aria-label', 'Dismiss notification');
    close.textContent = '×';

    copy.append(heading, body);
    toast.append(icon, copy, close);
    region.appendChild(toast);

    const dismiss = () => {
      toast.classList.remove('is-visible');
      window.setTimeout(() => toast.remove(), 220);
    };

    close.addEventListener('click', dismiss, { once: true });
    window.requestAnimationFrame(() => toast.classList.add('is-visible'));
    window.setTimeout(dismiss, type === 'error' ? 5200 : 3800);
  };

  const updateCartBadges = (itemCount) => {
    document.querySelectorAll(SELECTORS.cartBadge).forEach((badge) => {
      badge.textContent = String(itemCount);
      badge.hidden = itemCount <= 0;
      badge.classList.remove('vexx-cart-badge-bump');
      void badge.offsetWidth;
      badge.classList.add('vexx-cart-badge-bump');
      window.setTimeout(() => badge.classList.remove('vexx-cart-badge-bump'), 450);
    });
  };

  const refreshCartDrawerSection = async () => {
    const header = document.querySelector(SELECTORS.siteHeader);
    const sectionWrapper = header?.closest('[id^="shopify-section-"]');
    const sectionId = sectionWrapper?.id?.replace(/^shopify-section-/, '');

    if (!sectionId) return;

    const url = new URL(window.location.href);
    url.searchParams.set('sections', sectionId);

    const response = await fetch(url.toString(), {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) return;

    const sections = await response.json();
    const freshHeaderHtml = sections?.[sectionId];
    if (!freshHeaderHtml) return;

    const parsed = new DOMParser().parseFromString(freshHeaderHtml, 'text/html');
    const freshDrawer = parsed.querySelector(SELECTORS.cartDrawer);
    const currentDrawer = document.querySelector(SELECTORS.cartDrawer);
    const freshBadge = parsed.querySelector(SELECTORS.cartBadge);
    const currentBadge = document.querySelector(SELECTORS.cartBadge);

    if (freshDrawer && currentDrawer) {
      currentDrawer.innerHTML = freshDrawer.innerHTML;
    }

    if (freshBadge && currentBadge) {
      currentBadge.textContent = freshBadge.textContent;
      currentBadge.hidden = freshBadge.hidden;
    }
  };

  const refreshCartInterface = async () => {
    const cartResponse = await fetch(`${getShopRoot()}cart.js`, {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });

    if (!cartResponse.ok) throw new Error('Unable to refresh the bag.');

    const cart = await cartResponse.json();
    updateCartBadges(Number(cart.item_count) || 0);

    try {
      await refreshCartDrawerSection();
    } catch (error) {
      console.warn('VEXX cart drawer refresh warning:', error);
    }

    document.dispatchEvent(new CustomEvent('cart:updated', {
      bubbles: true,
      detail: { cart },
    }));

    return cart;
  };

  const parseErrorMessage = async (response) => {
    try {
      const data = await response.json();
      return data?.description || data?.message || 'This item could not be added.';
    } catch (error) {
      return 'This item could not be added. Please try again.';
    }
  };

  const handleProductSubmit = async (event) => {
    const form = event.target instanceof HTMLFormElement
      ? event.target.closest(SELECTORS.productForm)
      : null;

    if (!form) return;

    // Stop the older PDP and Quick View submit handlers from reloading or redirecting.
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (form.dataset.ajaxAdding === 'true') return;
    form.dataset.ajaxAdding = 'true';

    const button = form.querySelector(SELECTORS.submitButton);
    const originalText = button?.textContent?.trim() || 'ADD TO BAG';
    const productTitle = getProductTitle(form);

    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = 'ADDING...';
    }

    try {
      const response = await fetch(`${getShopRoot()}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });

      if (!response.ok) {
        throw new Error(await parseErrorMessage(response));
      }

      await response.json();
      const cart = await refreshCartInterface();

      if (button) {
        button.textContent = 'ADDED ✓';
      }

      showToast({
        type: 'success',
        title: 'ADDED TO YOUR BAG',
        message: `${productTitle} was added successfully. Bag total: ${cart.item_count} item${cart.item_count === 1 ? '' : 's'}.`,
      });

      window.setTimeout(() => {
        if (!button) return;
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.textContent = originalText;
      }, 1400);
    } catch (error) {
      console.error('VEXX add-to-cart error:', error);

      showToast({
        type: 'error',
        title: 'COULD NOT ADD ITEM',
        message: error instanceof Error ? error.message : 'Please try again.',
      });

      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.textContent = originalText;
      }
    } finally {
      form.dataset.ajaxAdding = 'false';
    }
  };

  // Capture phase is intentional: it safely overrides the old PDP and Quick View redirect handlers.
  document.addEventListener('submit', handleProductSubmit, true);
})();
